import { Injectable } from '@angular/core';

import { RestApiClientService } from '@core/rest-api-client/rest-api-client.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { fetchOccupationListByOnetCodesQuery } from '@pages/jobs/utils/jobs-detail.util';
import { IAppState } from '@state/state/app.state';
import { EntityType } from '@utils/enum/entity-type.enum';
import { genPopulateAttr } from '@utils/util';
import { Apollo, gql } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { IJob } from 'src/gen';
import {
  bankingJobKsasFetchFail,
  bankingJobKsasFetchSuccess,
  degreeProgramOccupationsFetch,
  degreeProgramOccupationsFetchFail,
  degreeProgramOccupationsFetchSuccess,
  fetchOccupationsSuccess,
  interestListFetch,
  interestListFetchFail,
  interestListFetchSuccess,
  interestSurveySubmit,
  interestSurveySubmitFail,
  interestSurveySubmitSuccess,
  jobFamilyOccupationsFetch,
  jobFamilyOccupationsFetchFail,
  jobFamilyOccupationsFetchSuccess,
  jobListFetch,
  jobListFetchFail,
  jobListFetchSuccess,
  militaryOccupationsFetch,
  militaryOccupationsFetchFail,
  militaryOccupationsFetchSuccess,
  occupationsFetch,
  occupationsFetchFail,
  occupationsFetchSuccess
} from './actions';
import {
  degreeProgramCipCodesSelector,
  interestsSelector,
  jobsSelector,
  militaryOccupationsOnetCodesSelector,
  occupationsOnetCodesSelector
} from './selectors';
import { isDegenerateInterestInput } from '../utils/is-degenerate-interest-input.util';

@Injectable()
export class JobListEffects {
  jobListFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobListFetch),
      switchMap(() => {
        const attributeLists = [['job_category'], ['image']];
        const populate = attributeLists.map<string>(it => genPopulateAttr(it)).join('&');
        return this.apiClient
          .fetchEntityCollection({
            entityType: EntityType.Jobs,
            populate,
            urlSearchParams: { 'pagination[limit]': 10000, 'sort[0]': 'title:asc' }
          })
          .pipe(
            map((data: any) =>
              jobListFetchSuccess({
                data: data.map(item => ({
                  ...item,
                  job_category: item?.job_category,
                  image: item?.image
                }))
              })
            ),
            catchError(response => of(jobListFetchFail({ error: response.error })))
          );
      })
    )
  );

  jobListFetchSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobListFetchSuccess),
      withLatestFrom(this.store$.select(jobsSelector)),
      switchMap(([, jobs]) =>
        this.fetchOccupationListByOnetCodes(
          jobs.filter(job => job.custom_id || job.onet_code).map(job => job.custom_id || job.onet_code)
        ).pipe(map(response => fetchOccupationsSuccess({ occupations: response })))
      )
    )
  );

  // Fetch KSAs for the banking-job corpus once after the job list loads.
  // The result is stored in state.bankingJobKsas and consumed by the
  // KSA-overlap source builder in the skills-match selector. We query the
  // pure O*NET codes (not custom_ids) because KSAs live on the parent
  // O*NET in PathBuilder; `occupationListByOnetCodes` returns them directly.
  bankingJobKsasFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobListFetchSuccess),
      withLatestFrom(this.store$.select(jobsSelector)),
      switchMap(([, jobs]) => {
        const uniqueOnets = Array.from(new Set(jobs.map(j => j.onet_code).filter((c): c is string => !!c)));
        return this.loadBankingJobKsas(uniqueOnets).pipe(
          map(data => bankingJobKsasFetchSuccess({ data })),
          catchError(response => of(bankingJobKsasFetchFail({ error: response.error })))
        );
      })
    )
  );

  interestListFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interestListFetch),
      switchMap(() =>
        this.loadInterests().pipe(
          map((data: any) => interestListFetchSuccess({ data })),
          catchError(response => of(interestListFetchFail({ error: response.error })))
        )
      )
    )
  );

  interestSurveySubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interestSurveySubmit),
      withLatestFrom(this.store$.select(interestsSelector)),
      switchMap(([_, questions]) =>
        this.saveInterestsSurvey(questions).pipe(
          withLatestFrom(this.store$.select(jobsSelector)),
          map(([{ rankedOccupations, userTraitScores }, jobs]) => {
            const scoreByOnet: Record<string, number> = Object.fromEntries(
              rankedOccupations.map(item => [item.onetCode, item.score])
            );
            const scoreMap: Record<string, number> = Object.fromEntries(
              jobs.map(job => {
                const score = scoreByOnet[job.onet_code] ?? -1;
                return [job.id, score];
              })
            );
            // Compute degenerate from user answers (not gateway scores) — see
            // is-degenerate-interest-input.util.ts for the rule.
            const degenerate = isDegenerateInterestInput(questions.map(q => q.answer));
            return interestSurveySubmitSuccess({ scoreMap, degenerate, userTraitScores });
          }),
          catchError(response => of(interestSurveySubmitFail({ error: response.error })))
        )
      )
    )
  );

  occupationsFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(occupationsFetch),
      withLatestFrom(
        this.store$.select(occupationsOnetCodesSelector),
        this.store$.select(militaryOccupationsOnetCodesSelector)
      ),
      switchMap(([, onetCodes, militaryOnetCodes]) =>
        this.loadOccupations([...onetCodes, ...militaryOnetCodes]).pipe(
          map((data: any) => occupationsFetchSuccess({ data })),
          catchError(response => of(occupationsFetchFail({ error: response.error })))
        )
      )
    )
  );

  jobFamilyOccupationsFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobFamilyOccupationsFetch),
      withLatestFrom(
        this.store$.select(occupationsOnetCodesSelector),
        this.store$.select(militaryOccupationsOnetCodesSelector)
      ),
      filter(
        ([, onetCodes, militaryOnetCodes]) =>
          (onetCodes && onetCodes.length > 0) || (militaryOnetCodes && militaryOnetCodes.length > 0)
      ),
      switchMap(([, onetCodes, militaryOnetCodes]) => {
        return this.loadJobFamilyOccupations([...onetCodes, ...militaryOnetCodes]).pipe(
          map((data: any) => jobFamilyOccupationsFetchSuccess({ data })),
          catchError(response => of(jobFamilyOccupationsFetchFail({ error: response.error })))
        );
      })
    )
  );

  degreeProgramOccupationsFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(degreeProgramOccupationsFetch),
      withLatestFrom(this.store$.select(degreeProgramCipCodesSelector)),
      filter(([, cipCodes]) => !!cipCodes.filter(Boolean).length),
      switchMap(([, cipCodes]) => {
        const cipCodesRequest = cipCodes.filter(Boolean);
        return this.loadDegreeProgramOccupations(cipCodesRequest).pipe(
          map((data: any) => degreeProgramOccupationsFetchSuccess({ data })),
          catchError(response => of(degreeProgramOccupationsFetchFail({ error: response.error })))
        );
      })
    )
  );

  militaryOccupationsFetchV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(militaryOccupationsFetch),
      switchMap(({ mocId }) =>
        this.loadMilitaryOccupations(mocId).pipe(
          map((data: any) => militaryOccupationsFetchSuccess({ data })),
          catchError(response => of(militaryOccupationsFetchFail({ error: response.error })))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly apiClient: RestApiClientService<IJob>,
    private readonly apollo: Apollo,
    private readonly store$: Store<IAppState>
  ) {}

  private loadInterests(): Observable<any[]> {
    const query = gql`
      query {
        getInterestSurveyQuestions {
          questionId
          question
          interestId
          interestName
          ordinal
        }
      }
    `;

    return this.apollo
      .query<any>({
        query
      })
      .pipe(
        map(response =>
          response.data.getInterestSurveyQuestions.map(question => ({
            ...question
          }))
        )
      );
  }

  private saveInterestsSurvey(
    questions: any[]
  ): Observable<{ rankedOccupations: { onetCode: string; score: number }[]; userTraitScores: Record<number, number> }> {
    const mutation = gql`
      mutation SaveInterestSurvey($input: InterestSurveyInput!) {
        saveInterestSurvey(input: $input) {
          userSurveyResults {
            traitId
            traitName
            score
            description
          }
          rankedOccupations {
            onetCode
            score
          }
        }
      }
    `;

    return this.apollo
      .mutate<any>({
        mutation,
        variables: {
          input: {
            tenantId: '98015873-fae2-42b4-9c32-031cb7f88308',
            userProfileId: '98015873-fae2-42b4-9c32-031cb7f88308',
            questions: questions.map(q => ({
              questionId: Number(q.questionId),
              answer: q.answer
            }))
          }
        }
      })
      .pipe(
        map(response => {
          const { rankedOccupations, userSurveyResults } = response.data.saveInterestSurvey;

          // Build interestId -> score map from userSurveyResults.
          // traitId is the numeric RIASEC ID (1-6) -- matches PathBuilder's
          // interestId. We use numeric IDs, never string names (gateway uses
          // "Enterprise" while PathBuilder uses "Enterprising").
          const userTraitScores: Record<number, number> = Object.fromEntries(
            (userSurveyResults ?? []).map((r: any) => [r.traitId, r.score])
          );

          return {
            rankedOccupations: rankedOccupations as { onetCode: string; score: number }[],
            userTraitScores
          };
        })
      );
  }

  private loadJobFamilyOccupations(onetCodes: string[]): Observable<any[]> {
    const query = gql`
      query ($onetCodes: [String]!) {
        getJobFamilyOccupations(onetCodes: $onetCodes) {
          onetCode
          jobFamilyOccupations {
            greatFit {
              onetCode
            }
            goodFit {
              onetCode
            }
            okFit {
              onetCode
            }
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { onetCodes }
      })
      .pipe(map(response => response.data.getJobFamilyOccupations.map(item => ({ ...item }))));
  }

  private loadOccupations(onetCodes: string[]): Observable<any[]> {
    const query = gql`
      query ($onetCodes: [ID]!) {
        occupationListByOnetCodes(onetCodes: $onetCodes) {
          onetCode
          onetTitle
          occupationKSAs {
            ksa {
              id
              name
              displayName
              type
            }
            importance
            level
          }
        }
      }
    `;

    return this.apollo
      .use('cpb')
      .query<any>({
        query,
        variables: { onetCodes }
      })
      .pipe(map(response => response.data.occupationListByOnetCodes.map(item => ({ ...item }))));
  }

  /**
   * Load KSAs for the banking-job corpus. Returns a map keyed by O*NET code
   * with the `occupationKSAs` array as value. Used by the KSA-overlap
   * synthetic experience source -- we only need `{ ksa.id, importance }`
   * per KSA for Jaccard computation, but pulling the full set keeps the
   * data available for any future consumers.
   */
  private loadBankingJobKsas(onetCodes: string[]): Observable<Record<string, any[]>> {
    if (!onetCodes?.length) {
      return of({});
    }
    const query = gql`
      query ($onetCodes: [ID]!) {
        occupationListByOnetCodes(onetCodes: $onetCodes) {
          onetCode
          occupationKSAs {
            ksa {
              id
            }
            importance
          }
        }
      }
    `;
    return this.apollo
      .use('cpb')
      .query<any>({
        query,
        variables: { onetCodes }
      })
      .pipe(
        map(response => {
          const result: Record<string, any[]> = {};
          for (const occ of response.data?.occupationListByOnetCodes ?? []) {
            if (occ?.onetCode) {
              result[occ.onetCode] = occ.occupationKSAs ?? [];
            }
          }
          return result;
        })
      );
  }

  private loadDegreeProgramOccupations(cipCodes: string[]): Observable<any[]> {
    const query = gql`
      query ($cipCodes: [String]!) {
        getDegreeProgramOccupations(cipCodes: $cipCodes) {
          cipCode
          distribution {
            greatFit {
              onetCode
            }
            goodFit {
              onetCode
            }
            okFit {
              onetCode
            }
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { cipCodes }
      })
      .pipe(map(response => response.data.getDegreeProgramOccupations.map(item => ({ ...item }))));
  }

  private loadEducationLevelOccupations(education_level_array: string[]): Observable<any[]> {
    const query = gql`
      query ($education_level_array: [EducationLevelEnum]!) {
        getEducationLevelOccupations(education_level_array: $education_level_array) {
          educationLevel
          distribution {
            greatFit {
              onetCode
            }
            goodFit {
              onetCode
            }
            okFit {
              onetCode
            }
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { education_level_array }
      })
      .pipe(
        map(response =>
          response.data.getEducationLevelOccupations.map(item => ({
            ...item
          }))
        )
      );
  }

  private loadMilitaryOccupations(mocId: number): Observable<any[]> {
    const query = gql`
      query ($mocId: Int!) {
        getOnetOccupationsFromMilitary_v2(mocId: $mocId) {
          onetCode
          onetTitle
          occupationKSAs {
            ksa {
              id
              name
              type
            }
            importance
            level
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { mocId }
      })
      .pipe(map(response => response?.data?.getOnetOccupationsFromMilitary_v2));
  }

  private fetchOccupationListByOnetCodes(onetCodes: string[]): Observable<any> {
    const query = fetchOccupationListByOnetCodesQuery();
    const uniqueOnetCodes = Array.from(new Set(onetCodes?.filter(code => !!code)));

    return this.apollo
      .use('cpb')
      .query<any>({
        query,
        variables: { onetCodes: uniqueOnetCodes }
      })
      .pipe(map(response => response.data.occupationsOrCustomByOnetCodes));
  }
}
